import { getUid } from "../auth/AuthManager";
import OnevocaServerError from "../common/OnevocaServerError";
import getApiInstance from "../common/api";
import Term from "../term/Term";
import LearnedTermsResult from "./LeanredTermsResult";
import LearningHistory from "./LearningHistory";
import LearningHistoryTermsResult from "./LearningHistoryTerms";
import LearningResult from "./LearningResult";
import { LearningType } from "./LearningType";
import { LearningHistoryRange } from "./learningHistoryRange";
import LearningContinueResult from "./LearningContinueResult";
import { LearningContinueRequest } from "./LearningContinueRequest";

function createFinishLearningParameters(
  uid: string,
  learningType: LearningType,
  correctTerms: Term[],
  incorrectTerms: Term[]
): any {
  const parameters: any = {};
  const correctTermParameters: any = {};
  const incorrectTermParameters: any = {};

  parameters["uid"] = uid;

  switch (learningType) {
    case LearningType.flashcards:
      parameters["game_type"] = "flash_card";
      break;
    case LearningType.multiplechoice:
      parameters["game_type"] = "multiple_choice";
      break;
    case LearningType.spellingQuiz:
      parameters["game_type"] = "spell_quiz";
      break;
  }

  correctTerms.forEach((term) => {
    correctTermParameters[term.id] = {
      index: term.index,
      id: term.id,
      exp: term.exp,
      true_and_false: true,
    };
  });

  incorrectTerms.forEach((term) => {
    incorrectTermParameters[term.id] = {
      index: term.index,
      id: term.id,
      exp: term.exp,
      true_and_false: false,
    };
  });

  parameters["words_correct"] = correctTermParameters;
  parameters["words_incorrect"] = incorrectTermParameters;

  return parameters;
}

export async function deleteLearningContinue(): Promise<void> {
  try {
    const uid = await getUid();
    const parameters: any = {};
    parameters["uid"] = uid;

    const response = await getApiInstance(undefined).post(
      `/learning/deleteLearningContinue.php`,
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    return;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateLearningContinue(
  options: LearningContinueRequest
): Promise<void> {
  try {
    const uid = await getUid();

    const parameters: any = {};
    parameters["uid"] = uid;
    parameters["learningType"] = options.learningType;
    parameters["learningGroup"] = options.learningGroup;
    parameters["learningLevel"] = options.learningLevel;
    parameters["seed"] = options.seed;
    parameters["sort"] = options.sort;
    parameters["itemSize"] = options.itemSize;
    parameters["currentIndex"] = options.currentIndex;
    parameters["currentPage"] = options.currentPage;

    const response = await getApiInstance(undefined).post(
      `/learning/updateLearningContinue.php`,
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    return;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchLearningContinue(): Promise<LearningContinueResult | null> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/learning/getLearningContinue.php?uid=${uid}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const data = response.data.result;
    if (data === undefined || data === null) {
      return Promise.resolve(null);
    }

    const result = LearningContinueResult.fromJson(data);
    return Promise.resolve(result);
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchLearningHistory(
  range: LearningHistoryRange
): Promise<LearningHistory> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/learning/getLearningTermsCount_v2.php?uid=${uid}&range=${range}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const data = response.data.data;
    if (data === undefined) {
      return Promise.reject("No data");
    }

    const history = LearningHistory.fromJson(data);
    history.range = range;
    return Promise.resolve(history);
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function updateLearningDailyTargetCount(
  count: number
): Promise<void> {
  try {
    const uid = await getUid();
    const parameters: any = {};
    parameters["uid"] = uid;
    parameters["value"] = count;

    const response = await getApiInstance(undefined).post(
      `/learning/updateLearningGoalValue.php`,
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    } else {
      return;
    }
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchMyFlames(): Promise<number> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/learning/getFlames.php?uid=${uid}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const flameCount = response.data.flameCount as number;
    return flameCount;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchLeanredTerms(
  range: LearningHistoryRange,
  type: "_correct" | "_incorrect"
): Promise<LearnedTermsResult> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/learning/getLearnedTerms_v2.php?uid=${uid}&range=${range}&type=${type}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const jsonData = response.data.data;
    if (jsonData === undefined) {
      return Promise.reject(new Error("No data."));
    }

    const terms = jsonData.map((termData: any) => Term.fromJson(termData));
    return new LearnedTermsResult(terms);
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function fetchLearningHistoryTerms(
  range: LearningHistoryRange
): Promise<LearningHistoryTermsResult> {
  try {
    const uid = await getUid();
    const response = await getApiInstance(undefined).get(
      `/learning/getLearningTermsCount_v2.php?uid=${uid}&range=${range}`
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const jsonData = response.data.data;
    if (jsonData === undefined) {
      return Promise.reject(new Error("No data."));
    }

    const result = LearningHistoryTermsResult.fromJson(jsonData);
    return result;
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function finishLearning(
  correctTerms: Term[],
  incorrectTerms: Term[],
  learningType: LearningType
): Promise<LearningResult> {
  try {
    const uid = await getUid();
    const parameters = createFinishLearningParameters(
      uid,
      learningType,
      correctTerms,
      incorrectTerms
    );
    const response = await getApiInstance(undefined).post(
      "/learning/updateLearningFinishedTermsLevelAndRecordHistory_v3.php",
      parameters
    );

    const error = response.data.error;
    if (error !== undefined) {
      return Promise.reject(new OnevocaServerError(error));
    }

    const termsData = response.data.data.terms;
    if (termsData === undefined) {
      return Promise.reject(new Error("No data."));
    }

    const terms = termsData.map((termData: any) => Term.fromJson(termData));
    const result = new LearningResult(terms);
    return result;
  } catch (error) {
    return Promise.reject(error);
  }
}
